import { pickBy } from 'lodash'
import qs from 'qs'
import api, { apiWithRetry, handleHttpStatus } from '@/utils/api.js'
import { defineStore } from 'pinia'
import { metaTagContent } from '@/helpers/documentHelpers.js'
import { removeTrailingSlash } from '@/utils/stringUtils.js'
import { deserializeJsonApi } from '@/utils/jsonApiDeserializer.js'
import { onMounted } from 'vue'
import { useElementVisibility, watchImmediate } from '@vueuse/core'
import { useAuthUserState } from '@/stores/authUser.js'
import { getItineraryRequest } from '@/stores/infoCollection.ts'
import { isFeatureEnabled } from '@/utils/featureUtils.js'
import { logErrorOnSentry } from '@/utils/errorReportingUtils.js'
import { isNetworkError } from 'axios-retry'
import {
  buildProjectionPreviewStorePatch,
  initialProjectionPreviewStoreState,
} from '@/modules/Itinerary/projectionPreviewStoreState'

export const useItineraryStore = defineStore('itinerary-page', {
  state: () => ({
    id: null,
    /** @type {import('../models').Product | import('../types').CustomItinerary | null} */
    data: null,
    /** @type {import('../types').ItineraryRequest | null} */
    itineraryRequest: null,
    itineraryRequestId: null,
    /** @type {string | null} */
    chatroomId: null,
    ...initialProjectionPreviewStoreState(),
    pricePanelIsVisible: true,
    sectionVisibility: {
      itinerary: null,
      accommodation: null,
      included: null,
      excluded: null,
      faq: null,
      addon: null,
      'customer-reviews': null,
    },
    chatEnabled: false,
    /** @type { { days: number[], slots: string[], ids: number[] } } */
    selectedTimeline: {
      days: [],
      slots: [],
      ids: [],
    },
    animation: {
      actionType: null,
      accommodation: {},
      activity: {},
    },
    /** @type { { ids: number[] } } */
    selectedAccommodations: {
      ids: [],
    },
    /** @type { 'started' | 'finished' | false } */
    onboarding: false,
    /** @type { { title: string, activities: any[], customize_messages: string[], preview_messages: string[], matched_country: string } } */
    landingMessages: null,
    /** @type { import('../types').CustomerReview[] } */
    reviews: [],
    loading: {
      reviews: false,
      data: false,
    },
    isReviewModalOpen: false,
    /** @type { string[]  } */
    customerReviewSummary: [],
  }),

  getters: {
    price({ data }) {
      return data.minimumCheckoutTierPricing?.priceCents ?? 0
    },
    currency({ data }) {
      return data.minimumCheckoutTierPricing?.currency || 'USD'
    },
    anySectionVisible(state) {
      return Object.values(state.sectionVisibility).some(Boolean)
    },
    discountedPrice({ data }) {
      if (this.isProjectionPreview) {
        return data.commercialPreview.currentPriceSubunits
      }
      if (data.currentEarlyBirdCoupon) {
        const { discountAmountCents, discountPercentage } = data.currentEarlyBirdCoupon
        if (discountAmountCents) return this.price - discountAmountCents
        if (discountPercentage) return (this.price * (100 - discountPercentage)) / 100
      }
      return null
    },
    isProjectionPreview({ projectionPreview }) {
      return projectionPreview !== null
    },
    timezone({ data }) {
      return data.baseTimezone || 'Etc/UTC'
    },
    isCustomItinerary: function () {
      return isFeatureEnabled('custom_itinerary') && this.data?.isCustomItinerary
    },
    displayReviews({ data }) {
      return data?.displayReviews !== false
    },
  },

  actions: {
    async initialize() {
      this.id = metaTagContent('itineraryId')
      const { data } = await api.get(`/api/products/${this.id}`)
      this.data = await deserializeJsonApi(data)
      this.chatroomId = this.data?.chatroomId ?? null

      if (this.displayReviews) this.fetchReviews({ productId: this.id, limit: 3 })
    },

    /** @param {unknown} previewInput */
    async initializeProjectionPreview(previewInput) {
      this.$patch(await buildProjectionPreviewStorePatch(previewInput))
    },

    async initializePremade() {
      this.loading.data = true
      await Promise.all([
        this.loadAirtableItinerary(metaTagContent('itineraryId')),
        this.loadItineraryRequest(metaTagContent('itineraryRequestId')),
      ]).catch(
        handleHttpStatus([401, 403, 404], () => {
          const authUser = useAuthUserState()
          if (authUser.email?.value) {
            alert('Failed to get data. It seems you are logged in with a different account, please login again')
            window.location.assign('/en/travelers')
          } else {
            const redirectUrl = `${window.location.pathname}${window.location.search}`
            window.location.assign(`/en/users/sign_in?redirect_url=${encodeURIComponent(redirectUrl)}`)
          }
        }),
      )

      this.loading.data = false

      if (this.itineraryRequest) {
        this.chatEnabled = true // chat modal only used on premade itinerary page.
      }

      this.fetchReviews({ itineraryId: this.id, limit: 3 })
    },

    async reload() {
      const data = await this.loadAirtableItinerary(this.id)

      data.itinerary.days.forEach(day => {
        day.activities.forEach(activity => {
          if (this.getIsPending({ type: 'activity', id: activity.id, day: activity.dayNumber })) {
            this.setAnimation({
              type: 'activity',
              actionType: this.getAnimationActionType(),
              id: activity.id,
              day: activity.dayNumber,
              status: 'animating',
            })
          }
        })
      })

      data.itinerary.days.forEach(day => {
        if (!day.accommodation) return

        if (this.getIsPending({ type: 'accommodation', id: day.accommodation.id, day: day.accommodation.day })) {
          this.setAnimation({
            type: 'accommodation',
            actionType: this.getAnimationActionType(),
            id: day.accommodation.id,
            day: day.accommodation.day,
            status: 'animating',
          })
        }
      })
    },

    apiBaseUrl() {
      return isCustomizedItinerary() ? '/api/traveler/custom_itineraries' : '/api/traveler/recommended_itineraries'
    },

    async loadAirtableItinerary(itineraryId) {
      this.id = itineraryId
      const params = new URLSearchParams(window.location.search)
      const { data } = await api.get(`${this.apiBaseUrl()}/${this.id}?${params.toString()}`)
      const deserializedData = await deserializeJsonApi(data)
      this.$deepPatch('data', deserializedData)
      return deserializedData
    },

    async loadItineraryRequest(requestId) {
      if (requestId) {
        this.itineraryRequestId = requestId
        this.itineraryRequest = await getItineraryRequest(requestId)
      }
    },

    async loadItineraryLandingMessages() {
      const itineraryId = metaTagContent('itineraryId')
      const itineraryRequestId = metaTagContent('itineraryRequestId')
      const endpoint = `${this.apiBaseUrl()}/${itineraryId}/landing_messages?itinerary_request_id=${itineraryRequestId}`
      const { data } = await api.get(endpoint)
      this.landingMessages = data
    },

    async getReserveButtonHtml() {
      const params = qs.parse(window.location.search.substring(1)) || {}
      const { pathname } = window.location
      if (!pathname) {
        return null
      }
      try {
        const { data } = await apiWithRetry.get(removeTrailingSlash(pathname) + '.js?' + qs.stringify(params))
        return data
      } catch (error) {
        // network failures are already reported by apiWithRetry's retry hook
        if (!isNetworkError(error)) logErrorOnSentry(error)
        return null
      }
    },

    monitorVisibility(key, selector) {
      onMounted(() => {
        const el = document.querySelector(selector)
        if (el) {
          const visible = useElementVisibility(el, { threshold: 0.1 })
          watchImmediate(visible, v => (this.sectionVisibility[key] = v))
        } else this.sectionVisibility[key] = null
      })
    },

    /** @type { (selectedTimeline: import('../types').SelectedTimeline) => boolean } */
    inSelectedTimeline({ day = null, slot = null, id = null }) {
      const { days, slots, ids } = this.selectedTimeline
      if (!day && !slot && !id) return false
      if (Object.values(this.selectedTimeline).flat().length === 0) return false
      if (day && days.length && !days.includes(day)) return false
      if (slot && slots.length && !slots.includes(slot)) return false
      if (id && ids.length && !ids.includes(id)) return false
      return true
    },
    /** @type { (animation: import('../types').ItineraryAnimationSetter) => void } */
    setAnimation({ type, actionType: action, id, day, status }) {
      this.animation[type][`${id}-${day}`] = status
      this.animation.actionType = action
    },
    /** @type { (animation: import('../types').ItineraryAnimationGetter) => boolean } */
    getIsAnimating({ type, id, day }) {
      return this.animation[type][`${id}-${day}`] === 'animating'
    },
    getAnimationActionType() {
      return this.animation.actionType
    },
    /** @type { (animation: import('../types').ItineraryAnimationGetter) => boolean } */
    getIsPending({ type, id, day }) {
      return this.animation[type][`${id}-${day}`] === 'pending'
    },
    /** @type { (productId: string | null, itineraryId: string | null) => Promise<void> } */
    async fetchReviews({ productId = null, itineraryId = null, limit = null }) {
      this.loading.reviews = true

      try {
        const { data } = await api.get(`/api/customer_reviews`, {
          params: pickBy({ product_id: productId, itinerary_id: itineraryId, limit: limit }),
        })

        const deserializedData = await deserializeJsonApi(data)
        this.reviews = deserializedData || []
        this.customerReviewSummary = data.meta.summary
      } catch (error) {
        console.error('Error fetching customer reviews:', error)
        this.reviews = []
      } finally {
        this.loading.reviews = false
      }
    },
    openReviewModel() {
      this.isReviewModalOpen = true
    },
    closeReviewModel() {
      this.isReviewModalOpen = false
    },
    /** @type { (params: import('../types').UpdateProductRequestDto) => Promise<void> } */
    async updateProduct({ productId, title, prelude, user }) {
      const payload = new FormData()
      if (title) payload.append('product[itinerary_title]', title)
      if (prelude) payload.append('product[itinerary_prelude]', prelude)

      if (user) {
        Object.keys(user).forEach(key => {
          if (user[key] !== undefined && user[key] !== null) {
            payload.append(`product[user][${key}]`, user[key])
          }
        })
      }

      /** @type { import('axios').AxiosResponse<import('../models').Product> } */
      const response = await api.patch(`/api/hero/products/${productId}`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      /** @type { import('../models').Product['itinerary']} */
      const itinerary = await deserializeJsonApi(response.data)

      this.$patch(state => {
        state.data.itinerary.preludeHtml = itinerary.preludeHtml
        state.data.itinerary.title = itinerary.title
      })

      const hero = itinerary.hero
      if (hero) {
        // Use $patch to ensure reactivity
        this.$patch(state => {
          if (state.data?.itinerary?.hero) {
            state.data.itinerary.hero.avatar = hero.avatar
            state.data.itinerary.hero.description = hero.description
            state.data.itinerary.hero.tiktokUsername = hero.tiktokUsername
            state.data.itinerary.hero.instagramUsername = hero.instagramUsername
            state.data.itinerary.hero.name = hero.name
            state.data.itinerary.hero.overrideHeroBioDisplay = hero.overrideHeroBioDisplay
          }
        })
      }
    },
    async updateItineraryImages({ productId, formData }) {
      const response = await api.post(`/api/hero/products/${productId}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      const itinerary = await deserializeJsonApi(response.data)
      this.$patch(state => {
        state.data.itinerary.images = itinerary.images
      })
    },
    /** @type { (productId: string|number) => Promise<void> } */
    async publishProduct(productId) {
      await api.put(`/api/hero/products/${productId}/publish`)
    },
  },
})

export function isCustomizedItinerary() {
  return location.pathname.includes('/customized-itinerary/')
}
